import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { inventoryAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
];

// ── Generic reference table section ──────────────────────────────────────────

const RefSection = ({ rows, columns, onAdd, onEdit, onDelete, loading }) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
      <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onAdd}>
        Add
      </Button>
    </Box>
    <TableContainer component={Paper} sx={tablePageTableContainerSx}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
            {columns.map((c) => <TableCell key={c.key}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && <TableSkeletonRows columns={columns.length + 1} />}
          {!loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                No entries yet
              </TableCell>
            </TableRow>
          ) : rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell align="center" sx={actionsKabobCellSx}>
                <RowActionsMenu
                  actions={[
                    { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => onEdit(row) },
                    { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => onDelete(row), color: 'error' },
                  ]}
                />
              </TableCell>
              {columns.map((c) => (
                <TableCell key={c.key}>{row[c.key] ?? '-'}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

// ── Tab definitions ───────────────────────────────────────────────────────────

const TABS = [
  {
    label: 'Item Types',
    columns: [{ key: 'name', label: 'Name' }],
    getAll: () => inventoryAPI.getItemTypes(),
    create: (d) => inventoryAPI.createItemType(d),
    update: (id, d) => inventoryAPI.updateItemType(id, d),
    remove: (id) => inventoryAPI.deleteItemType(id),
    emptyForm: { name: '' },
    fields: [{ key: 'name', label: 'Name', required: true }],
  },
  {
    label: 'Units',
    columns: [{ key: 'name', label: 'Name' }, { key: 'abbreviation', label: 'Abbreviation' }],
    getAll: () => inventoryAPI.getUnits(),
    create: (d) => inventoryAPI.createUnit(d),
    update: (id, d) => inventoryAPI.updateUnit(id, d),
    remove: (id) => inventoryAPI.deleteUnit(id),
    emptyForm: { name: '', abbreviation: '' },
    fields: [
      { key: 'name', label: 'Name', required: true },
      { key: 'abbreviation', label: 'Abbreviation' },
    ],
  },
  {
    label: 'Suppliers',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'contact', label: 'Contact' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
    ],
    getAll: () => inventoryAPI.getSuppliers(),
    create: (d) => inventoryAPI.createSupplier(d),
    update: (id, d) => inventoryAPI.updateSupplier(id, d),
    remove: (id) => inventoryAPI.deleteSupplier(id),
    emptyForm: { name: '', contact: '', phone: '', email: '', address: '', city: '', state: '' },
    fields: [
      { key: 'name', label: 'Name', required: true },
      { key: 'contact', label: 'Contact' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State', type: 'select', options: US_STATES },
    ],
  },
  {
    label: 'Manufacturers',
    columns: [{ key: 'name', label: 'Name' }],
    getAll: () => inventoryAPI.getManufacturers(),
    create: (d) => inventoryAPI.createManufacturer(d),
    update: (id, d) => inventoryAPI.updateManufacturer(id, d),
    remove: (id) => inventoryAPI.deleteManufacturer(id),
    emptyForm: { name: '' },
    fields: [{ key: 'name', label: 'Name', required: true }],
  },
];

// ── Main page ─────────────────────────────────────────────────────────────────

const AdminInventoryPage = () => {
  const notify = useNotify();
  const [tabIndex, setTabIndex] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');

  const tab = TABS[tabIndex];

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await tab.getAll();
      setRows(toArray(res.data, 'admin inventory rows'));
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [tabIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadRows(); }, [loadRows]);

  const handleTabChange = (_, newIndex) => {
    setTabIndex(newIndex);
  };

  const openAdd = () => {
    setEditing(null);
    setFormData({ ...tab.emptyForm });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const fd = {};
    tab.fields.forEach((f) => { fd[f.key] = row[f.key] ?? ''; });
    setFormData(fd);
    setError('');
    setDialogOpen(true);
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (row) => setDeleteTarget(row);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await tab.remove(deleteTarget.id);
      setDeleteTarget(null);
      loadRows();
    } catch (e) {
      notify.error(errorMessage(e, 'Delete failed'));
    }
  };

  const handleSubmit = async () => {
    setError('');
    try {
      if (editing) {
        await tab.update(editing.id, formData);
      } else {
        await tab.create(formData);
      }
      setDialogOpen(false);
      loadRows();
    } catch (e) {
      setError(e?.response?.data?.error || 'Save failed');
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box sx={{ mb: 1, pt: 0.5 }}>
          <Typography variant="h4">Admin — Inventory</Typography>
        </Box>
        <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 1 }}>
          {TABS.map((t) => <Tab key={t.label} label={t.label} />)}
        </Tabs>
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <RefSection
          rows={rows}
          columns={tab.columns}
          onAdd={openAdd}
          onEdit={openEdit}
          onDelete={handleDeleteClick}
          loading={loading}
        />
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? `Edit ${tab.label.slice(0, -1)}` : `Add ${tab.label.slice(0, -1)}`}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {tab.fields.map((f) =>
              f.type === 'select' ? (
                <Autocomplete
                  key={f.key}
                  fullWidth
                  size="small"
                  disableClearable
                  options={[{ value: '', label: 'None' }, ...f.options]}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.value === v?.value}
                  filterOptions={createPrefixFilter((o) => o.label)}
                  value={
                    [{ value: '', label: 'None' }, ...f.options].find((o) => o.value === (formData[f.key] ?? ''))
                    ?? { value: '', label: 'None' }
                  }
                  onChange={(e, newValue) =>
                    setFormData((prev) => ({ ...prev, [f.key]: newValue?.value ?? '' }))
                  }
                  renderInput={(params) => <TextField {...params} label={f.label} required={f.required} />}
                />
              ) : (
                <TextField
                  key={f.key}
                  name={f.key}
                  label={f.label}
                  value={formData[f.key] ?? ''}
                  onChange={handleFieldChange}
                  required={f.required}
                  fullWidth
                  size="small"
                />
              )
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Delete ${tab.label.slice(0, -1)}`}
        message={`Permanently delete "${deleteTarget?.name}" from ${tab.label}? This cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default AdminInventoryPage;
